import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router';

import InfoIcon from '@mui/icons-material/Info';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { Alert, AlertTitle, Box, Button, Link } from '@mui/material';
import {
  AnalyticsEventName,
  BestUserPromotionPermissions,
  Choice,
  CreatePromotionTemplateInputType,
  FinalChoice,
  GetAvailablePromotionsOptionsTreeResponse,
  PromotionFilterRules,
  PromotionSectionKeys,
  Section,
  SelectedItemsCityConfig,
} from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryCampaignProvider/apis';
import {
  DiscountTarget,
  TypeOfDiscount,
  PromotionCopyPrefillParams,
} from '@provider-portal/common/api/types';
import { useMount } from '@provider-portal/common/hooks/useMount';
import { Colors } from '@provider-portal/common/styles/bolt-uikit-colors';
import { FONT_FAMILY } from '@provider-portal/common/styles/themeMui';
import type { Nullable } from '@provider-portal/common/types/types';
import { Promotion, PromotionFooter } from 'promotions/components';

import { useURLQuery } from 'hooks/useURLQuery';
import { $tk } from 'utils/appTranslationKey';

import { useSelectedProviderCurrency } from 'root/hooks/useSelectedProvider';
import { usePromotionsApiFetcher } from 'root/promotions/providers/apiClient/PromotionsApiFetchersProvider';
import { SelectedMenuItem } from 'root/promotions/types';

import { FocusPreSelection, WizardSection } from './WizardSection';

const DOUBLE_DEALS_TAG: string = 'double_deals';

export interface PrefillParams {
  type_of_discount: TypeOfDiscount;
  discount_target: DiscountTarget;
  discount_amount?: number;
  use_only_trade_event_promotions?: boolean;
  trade_event_offer_id?: number;
}

interface Props {
  providerId: Nullable<number>;
  setSelectedMenuItems: React.Dispatch<React.SetStateAction<SelectedMenuItem[]>>;
  showMenuItemSelector: boolean;
  onClickNext: (promotion: Promotion) => void;
  onClickBack: () => void;
  setFilter: (choice: Choice) => void;
  promoOptionsTreeResponse: GetAvailablePromotionsOptionsTreeResponse | null;
  copyPrefillParams: PromotionCopyPrefillParams;
  hideDescriptionForTypeOfDiscountSection?: boolean;
  preSelectedFilterDiscountType?: TypeOfDiscount;
  prefillParams?: PrefillParams;
}

const styles = {
  alertBanner: {
    fontFamily: FONT_FAMILY,
    fontSize: '.875rem',
    lineHeight: '1.25rem',
    fontWeight: 400,
    color: Colors.neutral900,
    borderRadius: '8px',
    backgroundColor: Colors.red100,
  },
  alertBannerIcon: {
    color: Colors.red500,
  },
};

interface VisibleSection {
  choiceIndex: number | null;
  section: Section;
}

const SelectedItemsInfo: React.FC = () => {
  const intl = useIntl();
  return (
    <Alert severity="info" icon={<InfoIcon />} variant="outlined">
      <AlertTitle>
        <b>{intl.formatMessage({ id: $tk.components.AddPromotion.selectedItemsAlert.title })}</b>
      </AlertTitle>

      {intl.formatMessage(
        { id: $tk.components.AddPromotion.selectedItemsAlert.description },
        {
          link: (
            <Link
              href="https://foodpartner.bolt.eu/menu-editor/items"
              target="_blank"
              rel="noreferrer"
            >
              {intl.formatMessage({
                id: $tk.components.AddPromotion.selectedItemsAlert.menuEditor,
              })}
            </Link>
          ),
          break: <br />,
        },
      )}
    </Alert>
  );
};

const DoubleDealsInfo: React.FC = () => {
  const intl = useIntl();
  return (
    <Alert severity="info" icon={<InfoIcon />} variant="outlined">
      <AlertTitle>
        <b>{intl.formatMessage({ id: $tk.components.AddPromotion.doubleDealsAlert.title })}</b>
      </AlertTitle>

      {intl.formatMessage(
        { id: $tk.components.AddPromotion.doubleDealsAlert.description },
        {
          link: (
            <Link
              href="https://foodpartner.bolt.eu/menu-editor/items"
              target="_blank"
              rel="noreferrer"
            >
              {intl.formatMessage({
                id: $tk.components.AddPromotion.selectedItemsAlert.menuEditor,
              })}
            </Link>
          ),
          break: <br />,
        },
      )}
    </Alert>
  );
};

function useStrings() {
  const intl = useIntl();

  return useMemo(
    () => ({
      buttons: {
        continue: intl.formatMessage({ id: $tk.buttons.continue }),
        cancel: intl.formatMessage({ id: $tk.buttons.cancel }),
      },
      title: intl.formatMessage({ id: $tk.components.PromotionsList.title }),
      selectedItemsAlert: {
        title: intl.formatMessage({ id: $tk.components.AddPromotion.selectedItemsAlert.title }),
        description: intl.formatMessage({
          id: $tk.components.AddPromotion.selectedItemsAlert.description,
        }),
      },
    }),
    [intl],
  );
}

const BelowMinPriceAlert = (props: { minimumTotalPrice: number; currency: string }) => {
  const intl = useIntl();
  const alertMessage = intl.formatMessage(
    {
      id: $tk.components.promotionsSignUp.belowMinPriceValue,
    },
    {
      minimumTotalPrice: <strong>{props.minimumTotalPrice}</strong>,
      currency: <strong>{props.currency}</strong>,
    },
  );
  return (
    <Alert
      severity="info"
      icon={<WarningRoundedIcon sx={styles.alertBannerIcon} />}
      sx={styles.alertBanner}
    >
      {alertMessage}
    </Alert>
  );
};

const BelowMinItemsCountAlert = (props: { minimumItemsCount: number }) => {
  const intl = useIntl();
  const alertMessage = intl.formatMessage(
    {
      id: $tk.components.promotionsSignUp.belowMinItemsCount,
    },
    {
      minimumItemsCount: <strong>{props.minimumItemsCount}</strong>,
    },
  );
  return (
    <Alert
      severity="info"
      icon={<WarningRoundedIcon sx={styles.alertBannerIcon} />}
      sx={styles.alertBanner}
    >
      {alertMessage}
    </Alert>
  );
};
const collator = new Intl.Collator([], { numeric: true });

export function findFinalChoiceByTrait(
  section: Section,
  trait: string,
): [FinalChoice | null, VisibleSection[]] {
  const sortedChoices = section.choices.sort((a, b) => collator.compare(a.value, b.value));
  for (const [index, choice] of sortedChoices.entries()) {
    if (choice.type === CreatePromotionTemplateInputType.FINAL && choice.trait === trait) {
      return [choice, [{ choiceIndex: index, section }]];
    }
    if (
      choice.type === CreatePromotionTemplateInputType.DROPDOWN ||
      choice.type === CreatePromotionTemplateInputType.SECTION
    ) {
      const [result, dropdownTrace] = findFinalChoiceByTrait(choice.section, trait);
      if (result) {
        return [result, [{ choiceIndex: index, section }, ...dropdownTrace]];
      }
    }
  }
  return [null, null];
}

export function SelectPromotionWizard({
  onClickNext,
  onClickBack,
  setFilter,
  setSelectedMenuItems,
  showMenuItemSelector,
  hideDescriptionForTypeOfDiscountSection,
  promoOptionsTreeResponse,
  copyPrefillParams,
  prefillParams,
}: Props) {
  const intl = useIntl();
  const strings = useStrings();
  const campaignMetaFetcher = usePromotionsApiFetcher().fetchSelfServiceCampaignMeta.useFetcher();
  const analyticsFetcher = usePromotionsApiFetcher().storeAnalyticsEvent.useFetcher();
  const providerCurrency = useSelectedProviderCurrency();

  const location = useLocation();
  const history = useHistory();

  const [visibleSections, setVisibleSections] = useState<VisibleSection[]>(() => []);
  const [finalChoice, setFinalChoice] = useState<FinalChoice | undefined>(undefined);
  const [copiedProductIds, setCopiedProductIds] = useState<string[]>([]);
  const [isSelectedItemsOptionSelected, setIsSelectedItemsOptionSelected] = useState(false);
  const [menuItemsSelected, setMenuItemsSelected] = useState<SelectedMenuItem[]>([]);
  const [bannedUntil, setBannedUntil] = useState<number | undefined>(undefined);
  const [exclusionTags, setExclusionTags] = useState<string[]>([]);
  const [selectedItemsConfig, setSelectedItemsConfig] = useState<SelectedItemsCityConfig>({});
  const [bestUserPromotionsPermission, setBestUserPromotionsPermission] =
    useState<BestUserPromotionPermissions>({
      is_allowed_to_create_best_user_promotion: false,
      is_allowed_to_see_best_user_promotion: false,
    });
  const urlQuery = useURLQuery();

  useMount(() => {
    if (promoOptionsTreeResponse) {
      setBannedUntil(promoOptionsTreeResponse.banned_until);
      setExclusionTags(promoOptionsTreeResponse.exclusion_tags);
      if (promoOptionsTreeResponse.selected_items_config) {
        setSelectedItemsConfig(promoOptionsTreeResponse.selected_items_config);
      }
      if (promoOptionsTreeResponse.best_user_promotion_permissions) {
        setBestUserPromotionsPermission(promoOptionsTreeResponse.best_user_promotion_permissions);
      }
      setVisibleSections([{ choiceIndex: null, section: promoOptionsTreeResponse.options }]);

      // If preselectPromotionByTrait set for copy promotion feature
      if (copyPrefillParams.preselect_by_trait) {
        const [filteredFinalChoice, visitedSections] = findFinalChoiceByTrait(
          promoOptionsTreeResponse.options,
          copyPrefillParams.preselect_by_trait,
        );

        if (filteredFinalChoice) {
          setFinalChoice(filteredFinalChoice);
          setVisibleSections(visitedSections);
          visitedSections.forEach((section) => {
            if (section.section.key === PromotionSectionKeys.TYPE_OF_DISCOUNT) {
              setIsSelectedItemsOptionSelected(
                section.section.choices[section.choiceIndex].key ===
                  PromotionFilterRules.TYPE_OF_DISCOUNT_SELECTED_ITEMS,
              );
            }
            setFilter(section.section.choices[section.choiceIndex]);
          });
        }
      }
    }
  });

  const shouldShowMenuItemSelector = useMemo(() => {
    return showMenuItemSelector && isSelectedItemsOptionSelected;
  }, [isSelectedItemsOptionSelected, showMenuItemSelector]);

  useEffect(() => {
    setSelectedMenuItems(menuItemsSelected);
  }, [menuItemsSelected, setSelectedMenuItems]);

  useMount(() => {
    analyticsFetcher
      .fetch({
        event: { type: AnalyticsEventName.PROMOTION_CREATION_PAGE_VISITED },
      })
      .finally(() => {});
  });

  const isPassingMinPriceCriteria = useMemo(() => {
    let totalPrice = 0;
    menuItemsSelected.forEach((item) => {
      totalPrice += item.price;
    });
    return (
      !showMenuItemSelector ||
      !selectedItemsConfig.total_items_min_price ||
      (selectedItemsConfig.total_items_min_price &&
        selectedItemsConfig.total_items_min_price <= totalPrice)
    );
  }, [showMenuItemSelector, menuItemsSelected, selectedItemsConfig]);

  useEffect(() => {
    if (
      showMenuItemSelector &&
      copyPrefillParams.from_campaign_log_id &&
      !Number.isNaN(Number(copyPrefillParams.from_campaign_log_id))
    ) {
      campaignMetaFetcher
        .fetch({ self_service_campaign_log_id: Number(copyPrefillParams.from_campaign_log_id) })
        .then((promotionMeta) => {
          setCopiedProductIds(
            (promotionMeta.result ?? { menu_items_info: [] })?.menu_items_info?.map(
              (item) => item.product_id ?? item.global_stable_context_id,
            ),
          );
        });
    }
  }, [
    campaignMetaFetcher,
    copyPrefillParams.from_campaign_log_id,
    showMenuItemSelector,
    setCopiedProductIds,
  ]);

  const isPassingMinSelectedItemsCriteria = useMemo(() => {
    if (!showMenuItemSelector) {
      return true;
    }
    if (!selectedItemsConfig.min_items_count) {
      return menuItemsSelected.length >= 1;
    }
    return (
      !selectedItemsConfig.min_items_count ||
      (selectedItemsConfig.min_items_count &&
        selectedItemsConfig.min_items_count <= menuItemsSelected.length)
    );
  }, [showMenuItemSelector, menuItemsSelected, selectedItemsConfig]);

  const isSelectedItemsDiscount = useMemo(() => {
    return finalChoice && 'selected_items_discount_tag' in finalChoice;
  }, [finalChoice]);

  const shouldContinue = useMemo(() => {
    return (
      finalChoice !== undefined &&
      (!isSelectedItemsDiscount ||
        (isSelectedItemsDiscount && isPassingMinPriceCriteria && isPassingMinSelectedItemsCriteria))
    );
  }, [
    finalChoice,
    isPassingMinPriceCriteria,
    isPassingMinSelectedItemsCriteria,
    isSelectedItemsDiscount,
  ]);

  const handleClickContinue = useCallback(() => {
    if (!shouldContinue) {
      return;
    }

    onClickNext({
      title: finalChoice.title?.value ?? '',
      description: finalChoice.description?.value ?? '',
      startDate: finalChoice.start_date * 1000,
      endDate: finalChoice.end_date * 1000,
      trait: finalChoice.trait,
      campaignBonusType: finalChoice.campaign_bonus_type,
      trade_event_offer_data: finalChoice.trade_event_offer_data,
    });
  }, [finalChoice, onClickNext, shouldContinue]);

  const [preSelectionFilters, setPreSelectionFilters] = useState<FocusPreSelection>({
    type_of_discount: undefined,
    discount_targeting: undefined,
    discount_value_percentage: undefined,
  });

  const handleChoiceSelect = useCallback(
    (sectionIndex: number, choiceIndex: number, choice: Choice) => {
      setVisibleSections((prevValue) => {
        const nextValue = prevValue.slice(0, sectionIndex + 1);
        nextValue.push({ ...nextValue.pop(), choiceIndex });
        if (
          choice.type !== CreatePromotionTemplateInputType.FINAL &&
          choice.type !== CreatePromotionTemplateInputType.SELECT_DISABLED
        ) {
          nextValue.push({ choiceIndex: null, section: choice.section });
        }
        return nextValue;
      });

      if (choice.type === CreatePromotionTemplateInputType.FINAL) {
        setFinalChoice(choice);
      } else {
        setFinalChoice(undefined);
      }
      setPreSelectionFilters((prevValue) => {
        if (
          choice.key === PromotionFilterRules.TYPE_OF_DISCOUNT_FREE_DELIVERY ||
          choice.key === PromotionFilterRules.TYPE_OF_DISCOUNT_FULL_MENU ||
          choice.key === PromotionFilterRules.TYPE_OF_DISCOUNT_DOUBLE_DEAL
        ) {
          return {
            type_of_discount: choice.value,
            discount_targeting: prevValue.discount_targeting,
            discount_value_percentage: prevValue.discount_value_percentage,
          };
        }
        if (
          choice.key === PromotionFilterRules.DISCOUNT_TARGETING_ALL_CUSTOMERS ||
          choice.key === PromotionFilterRules.DISCOUNT_TARGETING_NEW_CUSTOMERS ||
          choice.key === PromotionFilterRules.DISCOUNT_TARGETING_CHURNED_CUSTOMERS ||
          choice.key === PromotionFilterRules.DISCOUNT_TARGETING_RETURNING_CUSTOMERS ||
          choice.key === PromotionFilterRules.DISCOUNT_TARGETING_BEST_CUSTOMERS
        ) {
          return {
            type_of_discount: prevValue.type_of_discount,
            discount_targeting: choice.value,
            discount_value_percentage: prevValue.discount_value_percentage,
          };
        }

        if (
          choice.key === PromotionFilterRules.DISCOUNT_VALUE_PERCENTAGE ||
          choice.key === PromotionFilterRules.DOUBLE_DEAL_VALUE
        ) {
          return {
            type_of_discount: prevValue.type_of_discount,
            discount_targeting: prevValue.discount_targeting,
            discount_value_percentage: choice.value,
          };
        }
        return prevValue;
      });
    },
    [],
  );

  useEffect(() => {
    visibleSections.forEach((visibleSection) => {
      if (visibleSection.choiceIndex !== null) {
        setFilter(visibleSection.section.choices[visibleSection.choiceIndex]);
      }
    });
  }, [setFilter, visibleSections]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (urlQuery.get('copyPrefill')) {
      queryParams.delete('copyPrefill');
      history.replace({
        search: queryParams.toString(),
      });
    }

    if (!prefillParams) {
      return;
    }

    if (urlQuery.get('prefill')) {
      queryParams.delete('prefill');
      history.replace({
        search: queryParams.toString(),
      });
    }

    const focusPreSelection: FocusPreSelection = {
      type_of_discount: prefillParams.type_of_discount,
      discount_targeting: prefillParams.discount_target,
      discount_value_percentage: prefillParams.discount_amount?.toString(),
    };
    if (
      focusPreSelection.type_of_discount === TypeOfDiscount.SELECTED_ITEMS ||
      focusPreSelection.type_of_discount === TypeOfDiscount.TRADE_EVENT
    ) {
      setIsSelectedItemsOptionSelected(true);
    }
    setPreSelectionFilters(focusPreSelection);
  }, [
    history,
    location.search,
    urlQuery,
    setIsSelectedItemsOptionSelected,
    setPreSelectionFilters,
    prefillParams,
  ]);

  const exclusionMessage = useMemo(() => {
    return intl.formatMessage({ id: $tk.components.AddPromotion.exclusionTooltipMessage });
  }, [intl]);

  return (
    <>
      <Box>
        {visibleSections.map(({ section, choiceIndex }, i) => (
          <WizardSection
            key={section.key}
            sectionIndex={i}
            choiceIndex={choiceIndex}
            section={section}
            onSelect={handleChoiceSelect}
            bestUserPromotionsPermission={bestUserPromotionsPermission}
            focusPreSelection={preSelectionFilters}
            bannedUntil={
              section.key === PromotionSectionKeys.TYPE_OF_DISCOUNT ? bannedUntil : undefined
            }
            showMenuItemSelector={
              section.key === PromotionSectionKeys.TYPE_OF_DISCOUNT
                ? shouldShowMenuItemSelector
                : false
            }
            setMenuItemsSelected={setMenuItemsSelected}
            setIsSelectedItemsOptionSelected={setIsSelectedItemsOptionSelected}
            preselectedProductIds={copiedProductIds}
            exclusionTags={exclusionTags}
            exclusionMessage={exclusionMessage}
            hideDescription={hideDescriptionForTypeOfDiscountSection}
          />
        ))}

        {!showMenuItemSelector &&
          finalChoice &&
          'selected_items_discount_tag' in finalChoice &&
          finalChoice.selected_items_discount_tag !== DOUBLE_DEALS_TAG && <SelectedItemsInfo />}
        {showMenuItemSelector &&
          isSelectedItemsDiscount &&
          finalChoice.selected_items_discount_tag !== DOUBLE_DEALS_TAG &&
          !isPassingMinSelectedItemsCriteria && (
            <Box mb={2}>
              <BelowMinItemsCountAlert
                minimumItemsCount={selectedItemsConfig.min_items_count ?? 1}
              />
            </Box>
          )}
        {showMenuItemSelector &&
          isSelectedItemsDiscount &&
          finalChoice.selected_items_discount_tag !== DOUBLE_DEALS_TAG &&
          isPassingMinSelectedItemsCriteria &&
          !isPassingMinPriceCriteria && (
            <Box mb={2}>
              <BelowMinPriceAlert
                minimumTotalPrice={selectedItemsConfig.total_items_min_price}
                currency={providerCurrency?.code ?? ''}
              />
            </Box>
          )}
        {!showMenuItemSelector &&
          finalChoice &&
          'selected_items_discount_tag' in finalChoice &&
          finalChoice.selected_items_discount_tag === DOUBLE_DEALS_TAG && <DoubleDealsInfo />}

        <PromotionFooter hideTacText>
          <Box mr={1}>
            <Button variant="contained" color="secondary" onClick={onClickBack}>
              {strings.buttons.cancel}
            </Button>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickContinue}
              disabled={!shouldContinue}
            >
              {strings.buttons.continue}
            </Button>
          </Box>
        </PromotionFooter>
      </Box>
    </>
  );
}
